import React, { useCallback, useEffect, useRef, useState } from "react";

/**
 * TextControlsToolbar (Bootstrap 5)
 * Toolbar ONLY (Paste / Undo / Redo / optional Cut) for an EXISTING <textarea> by id.
 * - Modern Clipboard API (no execCommand)
 * - Multi-step undo/redo with preserved selection
 * - Disables buttons contextually
 * - Emits native 'input' so React onChange runs
 */

export type TextControlsToolbarProps = {
  targetId: string;        // textarea id
  showCut?: boolean;       // default true
};

type Selection = { start: number; end: number };

type HistoryEntry = { value: string; sel: Selection };

function getSelection(ta: HTMLTextAreaElement): Selection {
  return { start: ta.selectionStart ?? 0, end: ta.selectionEnd ?? 0 };
}

function setSelection(ta: HTMLTextAreaElement, sel: Selection) {
  ta.selectionStart = sel.start; ta.selectionEnd = sel.end;
}

export default function TextAreaControls({ targetId, showCut = true }: TextControlsToolbarProps) {
  const taRef = useRef<HTMLTextAreaElement | null>(null);

  // History & flags
  const historyRef = useRef<HistoryEntry[]>([]);
  const indexRef = useRef<number>(-1);
  const applyingRef = useRef<boolean>(false); // suppress onInput while applying history

  // UI state
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [canCut, setCanCut] = useState(false);

  // Paste availability: feature detect once (no state updater to avoid lint warning)
  const canPaste = typeof navigator !== "undefined" && !!navigator.clipboard?.readText;

  // Push snapshot helper
  const pushSnapshot = useCallback((entry?: HistoryEntry) => {
    const ta = taRef.current; if (!ta) return;
    const e = entry ?? { value: ta.value, sel: getSelection(ta) };
    const upto = historyRef.current.slice(0, indexRef.current + 1);
    upto.push(e);
    if (upto.length > 200) upto.shift(); // cap length
    historyRef.current = upto;
    indexRef.current = upto.length - 1;
    setCanUndo(indexRef.current > 0);
    setCanRedo(false);
  }, []);

  const syncCutState = useCallback(() => {
    const ta = taRef.current; if (!ta) return;
    const sel = getSelection(ta);
    setCanCut(sel.end > sel.start);
  }, []);

  // Wire up to the <textarea>
  useEffect(() => {
    const el = document.getElementById(targetId) as HTMLTextAreaElement | null;
    taRef.current = el ?? null;
    if (!el) return;

    // seed history
    historyRef.current = [{ value: el.value ?? "", sel: { start: 0, end: 0 } }];
    indexRef.current = 0;
    setCanUndo(false); setCanRedo(false); syncCutState();

    const onInput = () => {
      // If input was triggered by undo/redo application, don't push (keeps redo chain)
      if (applyingRef.current) { applyingRef.current = false; return; }
      pushSnapshot();
      syncCutState();
    };

    const onSelect = () => syncCutState();

    el.addEventListener("input", onInput);
    el.addEventListener("select", onSelect);
    el.addEventListener("keyup", onSelect);
    el.addEventListener("mouseup", onSelect);

    return () => {
      el.removeEventListener("input", onInput);
      el.removeEventListener("select", onSelect);
      el.removeEventListener("keyup", onSelect);
      el.removeEventListener("mouseup", onSelect);
    };
  }, [pushSnapshot, syncCutState, targetId]);

  const emitInput = useCallback((ta: HTMLTextAreaElement) => {
    ta.dispatchEvent(new Event("input", { bubbles: true }));
  }, []);

  const handleUndo = useCallback(() => {
    const ta = taRef.current; if (!ta) return;
    if (indexRef.current <= 0) return;
    indexRef.current -= 1;
    const snap = historyRef.current[indexRef.current];
    applyingRef.current = true;
    ta.value = snap.value; setSelection(ta, snap.sel);
    emitInput(ta);
    setCanUndo(indexRef.current > 0);
    setCanRedo(indexRef.current < historyRef.current.length - 1);
  }, [emitInput]);

  const handleRedo = useCallback(() => {
    const ta = taRef.current; if (!ta) return;
    if (indexRef.current >= historyRef.current.length - 1) return;
    indexRef.current += 1;
    const snap = historyRef.current[indexRef.current];
    applyingRef.current = true;
    ta.value = snap.value; setSelection(ta, snap.sel);
    emitInput(ta);
    setCanUndo(indexRef.current > 0);
    setCanRedo(indexRef.current < historyRef.current.length - 1);
  }, [emitInput]);

  const handleCut = useCallback(async () => {
    const ta = taRef.current; if (!ta) return;
    const sel = getSelection(ta);
    if (sel.end <= sel.start) return;
    const selectedText = ta.value.substring(sel.start, sel.end);
    try {
      await navigator.clipboard.writeText(selectedText);
      pushSnapshot({ value: ta.value, sel });
      ta.value = ta.value.slice(0, sel.start) + ta.value.slice(sel.end);
      const caret: Selection = { start: sel.start, end: sel.start };
      setSelection(ta, caret);
      emitInput(ta);
      setCanUndo(true); setCanRedo(false); syncCutState();
    } catch (e) {
      // permissions error — ignore
    }
  }, [emitInput, pushSnapshot, syncCutState]);

  const handlePaste = useCallback(async () => {
    const ta = taRef.current; if (!ta) return;
    try {
      const text = await navigator.clipboard.readText();
      // sanitize without control-char regex (fixes no-control-regex warning)
      let clean = "";
      for (let i = 0; i < text.length; i++) {
        const code = text.charCodeAt(i);
        // allow tab (9), LF (10), CR (13); filter other C0 controls and DEL(127)
        if ((code >= 32 || code === 9 || code === 10 || code === 13) && code !== 127) clean += text[i];
      }
      const sel = getSelection(ta);
      pushSnapshot({ value: ta.value, sel });
      ta.value = ta.value.slice(0, sel.start) + clean + ta.value.slice(sel.end);
      const caret = sel.start + clean.length;
      setSelection(ta, { start: caret, end: caret });
      emitInput(ta);
      setCanUndo(true); setCanRedo(false); syncCutState();
    } catch (e) {
      // no clipboard permission
    }
  }, [emitInput, pushSnapshot, syncCutState]);

  // Keyboard shortcuts when textarea focused
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const ta = taRef.current; if (!ta) return;
      if (document.activeElement !== ta) return;
      const meta = e.metaKey || e.ctrlKey; if (!meta) return;
      const key = e.key.toLowerCase();
      if (key === "z" && !e.shiftKey) { e.preventDefault(); handleUndo(); }
      else if ((key === "z" && e.shiftKey) || key === "y") { e.preventDefault(); handleRedo(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleRedo, handleUndo]);

  return (
    <div className="bg-secondary-subtle m-0 py-1 pe-2 text-end">
      <div className="btn-group" role="group" aria-label="Text controls toolbar">
        {showCut && (
          <button type="button" className="btn btn-outline-secondary btn-sm border-light rounded-pill ms-2 px-3" onClick={handleCut} disabled={!canCut}>
            Cut
          </button>
        )}
        <button type="button" className="btn btn-outline-secondary btn-sm border-light rounded-pill ms-2 px-3" onClick={handlePaste} disabled={!canPaste}>
          Paste
        </button>

        <button type="button" className="btn btn-outline-secondary btn-sm border-light rounded-pill ms-2 px-3" onClick={handleUndo} disabled={!canUndo}>
          Undo
        </button>

        <button type="button" className="btn btn-outline-secondary btn-sm border-light rounded-pill ms-2 px-3" onClick={handleRedo} disabled={!canRedo}>
          Redo
        </button>
      </div>
    </div>
  );
}
